/**
 * Lecteur UBL 2.1 (Invoice / CreditNote) — XRechnung, Peppol BIS, UBL EN 16931.
 *
 * UBL utilise des agrégats (cac:) et des composants basiques (cbc:). Toutes
 * les recherches sont faites par nom local, sans dépendre du préfixe.
 */
import { emptyDocument, emptyParty, type InvoiceDocument, type InvoiceLine, type Party, type TaxBreakdown, type WarningSeverity } from '../model';
import { detectProfile } from '../profile';
import { attr, child, children, normalizeDate, num, path, rootName, text, txt } from './dom';

type Warn = (code: string, message: string, severity?: WarningSeverity) => void;

export function isUbl(doc: Document): boolean {
  const name = rootName(doc);
  return name === 'Invoice' || name === 'CreditNote';
}

function readParty(partyEl: Element | null): Party {
  const p = emptyParty();
  if (!partyEl) return p;
  p.name = txt(partyEl, 'PartyName', 'Name') ?? txt(partyEl, 'PartyLegalEntity', 'RegistrationName');
  p.tradingName = txt(partyEl, 'PartyName', 'Name');
  const endpoint = child(partyEl, 'EndpointID');
  const endpointValue = text(endpoint);
  if (endpointValue) p.electronicAddress = { scheme: attr(endpoint, 'schemeID') ?? '', value: endpointValue };

  for (const idEl of children(partyEl, 'PartyIdentification')) {
    const id = child(idEl, 'ID');
    const value = text(id);
    if (value) p.identifiers.push({ scheme: attr(id, 'schemeID') ?? '', value });
  }
  const legal = path(partyEl, 'PartyLegalEntity', 'CompanyID');
  p.legalRegistrationId = text(legal);
  if (p.legalRegistrationId && /^\d{14}$/.test(p.legalRegistrationId)) p.siret = p.legalRegistrationId;

  for (const ts of children(partyEl, 'PartyTaxScheme')) {
    const scheme = (txt(ts, 'TaxScheme', 'ID') ?? '').toUpperCase();
    const value = txt(ts, 'CompanyID');
    if (!value) continue;
    if (scheme === 'VAT') p.vatId = value;
    else if (scheme === 'FC' || scheme === 'TAX') p.taxId = value;
  }

  const addr = child(partyEl, 'PostalAddress');
  if (addr) {
    p.address = {
      line1: txt(addr, 'StreetName'),
      line2: txt(addr, 'AdditionalStreetName'),
      line3: txt(addr, 'AddressLine', 'Line'),
      postcode: txt(addr, 'PostalZone'),
      city: txt(addr, 'CityName'),
      country: txt(addr, 'Country', 'IdentificationCode'),
    };
  }
  const contact = child(partyEl, 'Contact');
  if (contact) {
    p.contact = {
      name: txt(contact, 'Name'),
      phone: txt(contact, 'Telephone'),
      email: txt(contact, 'ElectronicMail'),
    };
  }
  return p;
}

function readLine(el: Element): InvoiceLine {
  const line: InvoiceLine = {};
  line.id = txt(el, 'ID');
  line.note = txt(el, 'Note');
  const qtyEl = child(el, 'InvoicedQuantity') ?? child(el, 'CreditedQuantity') ?? child(el, 'Quantity');
  line.quantity = num(qtyEl);
  line.unitCode = attr(qtyEl, 'unitCode');
  line.lineNetAmount = num(child(el, 'LineExtensionAmount'));
  const item = child(el, 'Item');
  if (item) {
    line.name = txt(item, 'Name');
    line.description = txt(item, 'Description');
    line.sellerItemId = txt(item, 'SellersItemIdentification', 'ID');
    const taxCat = child(item, 'ClassifiedTaxCategory');
    if (taxCat) {
      line.vatCategory = txt(taxCat, 'ID');
      line.vatRate = num(child(taxCat, 'Percent'));
    }
  }
  // UBL 2.1 : cac:Price est frère de cac:Item dans la ligne (et non enfant).
  const price = child(el, 'Price');
  if (price) {
    line.unitPrice = num(child(price, 'PriceAmount'));
    line.priceBaseQuantity = num(child(price, 'BaseQuantity'));
    line.itemDiscount = num(path(price, 'AllowanceCharge', 'Amount'));
  }
  if (!line.vatCategory) {
    const sub = path(el, 'TaxTotal', 'TaxSubtotal', 'TaxCategory');
    line.vatCategory = txt(sub, 'ID');
    line.vatRate = num(child(sub, 'Percent'));
  }
  return line;
}

function pickTaxTotal(doc: Element, currency: string | undefined): Element[] {
  const totals = children(doc, 'TaxTotal');
  if (totals.length === 0) return [];
  const matching = totals.filter((t) => {
    const amount = child(t, 'TaxAmount');
    const cur = attr(amount, 'currencyID');
    return cur ? cur === currency : false;
  });
  const ordered = matching.length > 0 ? matching : totals;
  return ordered;
}

export function parseUbl(doc: Document, warn: Warn): InvoiceDocument {
  const out = emptyDocument('UBL');
  out.syntaxVersion = 'UBL 2.1';
  const root = doc.documentElement;

  // --- BT-23 / BT-24 ---
  const bt24 = txt(root, 'CustomizationID');
  out.profile = detectProfile(bt24);
  out.businessProcess = txt(root, 'ProfileID');
  if (bt24 == null) warn('PROFILE_UNKNOWN', 'BT-24 (CustomizationID) absent du XML UBL');
  else if (out.profile.profile === 'UNKNOWN') warn('PROFILE_UNKNOWN', `BT-24 non reconnu : ${bt24}`);

  // --- En-tête ---
  out.number = txt(root, 'ID');
  const typeCode = txt(root, 'InvoiceTypeCode') ?? txt(root, 'CreditNoteTypeCode');
  out.documentTypeCode = typeCode;
  if (rootNodeType(doc) === 'CreditNote') out.documentType = 'credit-note';
  else if (typeCode === '381') out.documentType = 'credit-note';
  else if (typeCode) out.documentType = 'invoice';
  out.issueDate = normalizeDate(txt(root, 'IssueDate'));
  out.dueDate = normalizeDate(txt(root, 'DueDate'));
  out.currency = txt(root, 'DocumentCurrencyCode');
  out.taxCurrency = txt(root, 'TaxCurrencyCode');
  out.buyerReference = txt(root, 'BuyerReference');
  out.orderReference = txt(root, 'OrderReference', 'ID');
  out.salesOrderReference = txt(root, 'OrderReference', 'SalesOrderID');
  out.contractReference = txt(root, 'ContractDocumentReference', 'ID');

  for (const note of children(root, 'Note')) {
    const value = text(note);
    if (value) out.notes.push(value);
  }

  const supplier = path(root, 'AccountingSupplierParty', 'Party');
  const customer = path(root, 'AccountingCustomerParty', 'Party');
  out.seller = readParty(supplier);
  out.buyer = readParty(customer);
  const payee = path(root, 'PayeeParty');
  if (payee) out.payee = readParty(payee);

  // --- Livraison (BT-72) ---
  out.deliveryDate =
    normalizeDate(txt(root, 'Delivery', 'ActualDeliveryDate')) ??
    normalizeDate(txt(root, 'Delivery', 'DeliveryDate')) ??
    normalizeDate(txt(root, 'DespatchDocumentReference', 'IssueDate'));

  // --- Paiement ---
  const means = child(root, 'PaymentMeans');
  if (means) {
    out.payment.meansCode = txt(means, 'PaymentMeansCode');
    out.payment.meansText = attr(child(means, 'PaymentMeansCode'), 'name');
    out.payment.iban = txt(means, 'PayeeFinancialAccount', 'ID');
    out.payment.bic = txt(means, 'PayeeFinancialAccount', 'FinancialInstitutionBranch', 'ID');
    out.payment.remittanceInformation = txt(means, 'PaymentID');
    out.payment.mandateReference = txt(means, 'PaymentMandate', 'ID');
    out.dueDate = normalizeDate(txt(means, 'PaymentDueDate')) ?? out.dueDate;
  }
  out.payment.dueDate = out.dueDate;
  out.payment.terms = txt(root, 'PaymentTerms', 'Note');

  // --- TVA (BG-23) ---
  out.taxes = pickTaxTotal(root, out.currency)
    .flatMap((t) => children(t, 'TaxSubtotal'))
    .map<TaxBreakdown>((s) => ({
      category: txt(s, 'TaxCategory', 'ID'),
      rate: num(child(path(s, 'TaxCategory'), 'Percent')),
      taxableAmount: num(child(s, 'TaxableAmount')),
      taxAmount: num(child(s, 'TaxAmount')),
      exemptionReason: txt(s, 'TaxCategory', 'TaxExemptionReason'),
      exemptionCode: txt(s, 'TaxCategory', 'TaxExemptionReasonCode'),
    }))
    .filter((t) => t.category || t.taxableAmount || t.taxAmount);

  // --- Totaux ---
  const totals = child(root, 'LegalMonetaryTotal') ?? child(root, 'LegalMonetaryTotal');
  if (totals) {
    out.totals.lineNet = num(child(totals, 'LineExtensionAmount'));
    out.totals.allowanceTotal = num(child(totals, 'AllowanceTotalAmount'));
    out.totals.chargeTotal = num(child(totals, 'ChargeTotalAmount'));
    out.totals.taxBasis = num(child(totals, 'TaxExclusiveAmount'));
    out.totals.grandTotal = num(child(totals, 'TaxInclusiveAmount'));
    out.totals.prepaid = num(child(totals, 'PrepaidAmount'));
    out.totals.rounding = num(child(totals, 'PayableRoundingAmount'));
    out.totals.duePayable = num(child(totals, 'PayableAmount'));
  }
  const taxTotals = pickTaxTotal(root, out.currency);
  const mainTax = taxTotals[0];
  if (mainTax) out.totals.taxTotal = num(child(mainTax, 'TaxAmount'));
  if (taxTotals.length > 1) out.totals.taxTotalAccounting = num(child(taxTotals[1], 'TaxAmount'));

  // --- Lignes ---
  const lineEls = [...children(root, 'InvoiceLine'), ...children(root, 'CreditNoteLine')];
  out.lines = lineEls.map(readLine);

  if (out.lines.length === 0) warn('EMPTY_LINES', 'Aucune ligne de facture dans le XML UBL');
  return out;
}

function rootNodeType(doc: Document): string {
  return rootName(doc);
}
